import { Copy, PhoneCall, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";

const errText = (e) => e?.response?.data?.detail || "Erreur, réessayez.";
const fr = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "jamais");

export default function RingoverSettingsPage() {
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [calls, setCalls] = useState([]);
  const load = () => {
    api.get("/ringover/config").then(({ data }) => setCfg(data)).catch((e) => (e?.response?.status === 403 ? setDenied(true) : toast.error(errText(e))));
    api.get("/ringover/calls", { params: { limit: 20 } }).then(({ data }) => setCalls(data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async (body, ok) => {
    setBusy(true);
    try { setCfg((await api.put("/ringover/config", body)).data); setKey(""); toast.success(ok); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const sync = async () => { try { toast.success((await api.post("/ringover/sync", { days: 7 })).data.message); setTimeout(load, 8000); } catch (e) { toast.error(errText(e)); } };
  const disconnect = async () => { if (window.confirm("Déconnecter Ringover ?")) setCfg((await api.delete("/ringover/config")).data); };

  if (denied) return <section className="page"><p className="pl-warning" data-testid="admin-only">Réservé aux administrateurs.</p></section>;
  if (!cfg) return <section className="page"><p className="pl-muted">Chargement…</p></section>;
  return (
    <section className="page" data-testid="ringover-settings-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">PARAMÈTRES</p>
          <h1>Ringover</h1>
          <p className="page-subtitle">Appels, enregistrements et transcriptions Ringover rattachés automatiquement aux opportunités (par numéro de téléphone), visibles dans l'historique de la fiche et ajoutés en commentaire dans noCRM.</p>
        </div>
        <span className={`status-badge ${cfg.configured ? "is-ok" : ""}`} data-testid="ringover-status">{cfg.configured ? "Connecté" : "Non connecté"}</span>
      </header>

      <section className="settings-section">
        <div className="section-title"><PhoneCall size={20} /><div><p className="eyebrow">1. CONNEXION</p><h2>Clé API Ringover</h2></div></div>
        <p className="fileprep-dedup-hint">Dans Ringover : Tableau de bord › Développeur › Créer une clé API. Cochez les droits « Appels » et « Empower » (pour la transcription, qui doit être incluse dans votre abonnement).</p>
        <div className="rgv-row">
          <input type="password" autoComplete="off" value={key} placeholder={cfg.configured ? "Clé enregistrée — collez-en une nouvelle pour la remplacer" : "Collez votre clé API Ringover"} onChange={(e) => setKey(e.target.value)} data-testid="ringover-api-key-input" />
          <button type="button" className="primary-button" disabled={busy || key.trim().length < 10} onClick={() => save({ api_key: key.trim() }, "Ringover connecté : synchronisation des 7 derniers jours lancée.")} data-testid="ringover-save-btn">{busy ? "Vérification…" : "Enregistrer et tester"}</button>
          {cfg.configured && <button type="button" className="secondary-button" onClick={disconnect} data-testid="ringover-disconnect-btn">Déconnecter</button>}
        </div>
        <label className="rgv-check"><input type="checkbox" checked={cfg.log_to_nocrm} onChange={(e) => save({ log_to_nocrm: e.target.checked }, "Réglage enregistré.")} data-testid="ringover-log-nocrm" /> Ajouter chaque appel (durée, résumé, lien d'enregistrement) en commentaire sur l'opportunité noCRM</label>
      </section>

      <section className="settings-section">
        <div className="section-title"><RefreshCw size={20} /><div><p className="eyebrow">2. SYNCHRONISATION</p><h2>Appels et transcriptions</h2></div></div>
        <div className="rgv-stats">
          <span><strong data-testid="ringover-calls-count">{cfg.calls}</strong> appels</span>
          <span><strong>{cfg.matched}</strong> rattachés à une opportunité</span>
          <span><strong>{cfg.transcribed}</strong> transcrits</span>
          <span>Dernière synchro : {fr(cfg.last_sync_at)}</span>
        </div>
        {cfg.last_error && <p className="pl-warning" data-testid="ringover-last-error">{cfg.last_error}</p>}
        <p className="fileprep-dedup-hint">Synchronisation automatique toutes les 15 minutes. Pour un rattachement immédiat après chaque appel, ajoutez ce webhook dans Ringover › Développeur › Webhooks (événements « Appel terminé » et « Transcription disponible ») :</p>
        <div className="rgv-row">
          <input readOnly value={cfg.webhook_url} data-testid="ringover-webhook-url" />
          <button type="button" className="secondary-button" onClick={() => navigator.clipboard.writeText(cfg.webhook_url).then(() => toast.success("Adresse copiée."), () => toast.error("Copie impossible : sélectionnez l'adresse et copiez-la manuellement."))} data-testid="ringover-copy-webhook"><Copy size={14} /> Copier</button>
          <button type="button" className="primary-button" disabled={!cfg.configured} onClick={sync} data-testid="ringover-sync-btn"><RefreshCw size={14} /> Synchroniser 7 jours</button>
        </div>
        {calls.length > 0 && (
          <div className="agent-table-wrap" style={{ marginTop: 16 }}>
            <table className="agent-table">
              <thead><tr><th>Date</th><th>Sens</th><th>Numéro</th><th>Commercial</th><th>Durée</th><th>Opportunité</th><th>Transcription</th></tr></thead>
              <tbody>{calls.map((c) => <tr key={c.call_id}><td>{fr(c.started_at)}</td><td>{c.direction === "in" ? "Entrant" : "Sortant"}</td><td>{c.contact_number || "—"}</td><td>{c.user_name || "—"}</td><td>{Math.round((c.duration || 0) / 60)} min</td><td>{c.lead_title || "—"}</td><td>{c.transcript_status === "ok" ? "Oui" : "—"}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}
